import { useEffect } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import SectionSuggested from '../PageBuilder/SectionBuilder/SectionSuggested';
import SectionFeatureGrid from '../PageBuilder/SectionBuilder/SectionFeatureGrid';
import SectionTopPicks from '../PageBuilder/SectionBuilder/SectionTopPicks/SectionTopPicks';
import SectionCategory from '../PageBuilder/SectionBuilder/SectionCategory/SectionCategory';
import SectionHeroCustom from '../PageBuilder/SectionBuilder/SectionHero/SectionHeroCustom';
import { ASSET_NAME, fetchTopPicksListings } from './LandingPage.duck';
import css from './LandingPage.module.css';
import mobileVideoSrc from '../../assets/repoze_website_light.mp4';
import desktopVideoSrc from '../../assets/repoze_website_animations_buy&sal_beige_4k_right.mp4';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    isAuthenticated,
    suggestedListings = [],
    suggestedListingsInProgress = false,
    featureGridListings = [],
    featureGridListingsInProgress = false,
    featureGridIsPersonalized = false,
    featureGridDebugInfo = null,
    topPicksListings = [],
    topPicksInProgress = false,
    topPicksFetched = false,
    onFetchTopPicks,
  } = props;

  // Fetch top picks client-side (requires Integration SDK via server API,
  // which is not available during SSR). Only fetch once per page load.
  useEffect(() => {
    if (!topPicksFetched && !topPicksInProgress && onFetchTopPicks) {
      onFetchTopPicks();
    }
  }, [topPicksFetched, topPicksInProgress, onFetchTopPicks]);

  useEffect(() => {
    // Add video background to the first section after component mounts
    const addVideoToFirstSection = () => {
      const firstSection = document.querySelector('#hero-panel');
      if (firstSection && !firstSection.querySelector(`.${css.sectionBackgroundVideo || 'section-background-video'}`)) {
        // Create video element
        const video = document.createElement('video');
        video.className = css.sectionBackgroundVideo || 'section-background-video';
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;

        // Track current video type to avoid unnecessary switches
        let currentVideoType = null; // Start with null to force initial load

        // Function to set the appropriate video source based on viewport
        const setVideoSource = (forceLoad = false) => {
          const isMediumOrLarger = window.innerWidth >= 768;
          const newVideoType = isMediumOrLarger ? 'desktop' : 'mobile';

          // Load initial video or switch if we've crossed the breakpoint threshold
          if (forceLoad || newVideoType !== currentVideoType) {
            const newSrc = isMediumOrLarger ? desktopVideoSrc : mobileVideoSrc;
            const currentTime = forceLoad ? 0 : video.currentTime;
            const wasPlaying = forceLoad ? false : !video.paused;

            video.src = newSrc;
            video.load();

            // Restore playback state (only for switches, not initial load)
            if (!forceLoad) {
              video.currentTime = currentTime;
              if (wasPlaying) {
                video.play().catch(() => {
                  // Handle autoplay restrictions gracefully
                });
              }
            }

            currentVideoType = newVideoType;
          }
        };

        // Set initial video source
        setVideoSource(true);

        // Debounced resize handler to prevent excessive switching
        let resizeTimeout;
        const handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(setVideoSource, 150);
        };

        window.addEventListener('resize', handleResize);

        // Store cleanup function on the video element
        video._cleanup = () => {
          window.removeEventListener('resize', handleResize);
          clearTimeout(resizeTimeout);
        };

        // Make sure the section has relative positioning
        firstSection.style.position = 'relative';
        firstSection.style.overflow = 'hidden';

        // Insert video as first child
        firstSection.insertBefore(video, firstSection.firstChild);
      }
    };

    // Try to add video immediately
    addVideoToFirstSection();

    // Also try after a short delay in case the section isn't rendered yet
    const timer = setTimeout(addVideoToFirstSection, 100);

    return () => {
      clearTimeout(timer);
      // Clean up resize listener if video was created
      const video = document.querySelector(`#section-1 .${css.sectionBackgroundVideo || 'section-background-video'}`);
      if (video && video._cleanup) {
        video._cleanup();
      }
    };
  }, [pageAssetsData]);

  // Add SuggestedPanel as a section to the page data
  const enhancedPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // If we have page data, reorder sections and add custom sections
  const pageDataWithSuggested = enhancedPageData ? {
    ...enhancedPageData,
    sections: (() => {
      const originalSections = enhancedPageData.sections || [];
      const reorderedSections = [];

      // Define the desired order of sections by ID
      const sectionOrder = [
        'hero-panel',
        'category-panel',
        'offers-panel',
        'suggested-panel',
        'feature-picks-panel',
        'feature-grid-panel',
        'hub-banner'
      ];

      // Create a map of original sections by their ID or name for easy lookup
      const sectionMap = new Map();
      const sectionsByName = new Map();
      originalSections.forEach(section => {
        if (section.sectionId) {
          sectionMap.set(section.sectionId, section);
        }
        // Also map by name for sections without IDs
        if (section.sectionName) {
          sectionsByName.set(section.sectionName.toLowerCase(), section);
        }
      });


      // Add sections in the specified order
      sectionOrder.forEach(sectionId => {
        if (sectionId === 'hero-panel') {
          // Check if hero-panel exists in original sections from CMS
          const cmsHeroSection = sectionMap.get('hero-panel');

          if (cmsHeroSection) {
            // Use data from CMS if available
            reorderedSections.push({
              ...cmsHeroSection,
              sectionType: 'customHero',
            });
          } else {
            // Fallback to hardcoded values if not in CMS
            reorderedSections.push({
              sectionType: 'customHero',
              sectionId: 'hero-panel',
              title: 'Make room\nfor magic',
              subtitle: 'Kick off your selling adventure and list products in seconds!',
              ctaText: 'Ready to get your repoze shop set up?',
              ctaButtonText: 'Start selling',
              ctaButtonLink: '/l/new',
            });
          }
        } else if (sectionId === 'category-panel') {
          // Add custom category panel
          reorderedSections.push({
            sectionType: 'customCategory',
            sectionId: 'category-panel',
          });
        } else if (sectionId === 'suggested-panel') {
          // Only add suggested panel if we have listings or are not loading
          // This prevents placeholder flashing
          if (suggestedListings.length > 0 || !suggestedListingsInProgress) {
            reorderedSections.push({
              sectionType: 'customSuggested',
              sectionId: 'suggested-panel',
              suggestedListings,
              visibleCards: 6,
              usePlaceholders: false, // Never use placeholders - only show real listings
            });
          }
        } else if (sectionId === 'hub-banner') {
          // Hub Banner section - try to find by ID first, then by name
          const hubBannerSection = sectionMap.get('hub-banner') || sectionsByName.get('hub banner');
          if (hubBannerSection) {
            reorderedSections.push({
              ...hubBannerSection,
              // Ensure it has an ID for rendering
              sectionId: hubBannerSection.sectionId || 'hub-banner',
            });
          }
        } else if (sectionId === 'feature-picks-panel') {
          // Top Picks section — always include in section order to avoid layout shifts.
          // TopPicksPanel handles rendering null when no listings are available.
          reorderedSections.push({
            sectionType: 'customTopPicks',
            sectionId: 'feature-picks-panel',
            topPicksListings,
          });
        } else if (sectionId === 'feature-grid-panel') {
          // Only add feature grid panel if we have listings or are not loading
          // This prevents placeholder flashing
          if (featureGridListings.length > 0 || !featureGridListingsInProgress) {
            reorderedSections.push({
              sectionType: 'customFeatureGrid',
              sectionId: 'feature-grid-panel',
              gridItems: featureGridListings, // Pass real listings instead of placeholder items
              columns: 4,
              rows: 3, // Show 12 items (4x3)
              usePlaceholders: false, // Never use placeholders - only show real listings
              isPersonalized: featureGridIsPersonalized, // Show personalized title if based on search history
              debugInfo: featureGridDebugInfo, // Debug info for testing personalization
            });
          }
        } else if (sectionMap.has(sectionId)) {
          // Add original section if it exists
          reorderedSections.push(sectionMap.get(sectionId));
        }
      });

      // Add any remaining sections that weren't in our ordered list
      // This ensures all CMS sections appear even if not explicitly ordered
      originalSections.forEach((section, index) => {
        // Skip sections that are already added (by ID or by name)
        const isAlreadyAdded = section.sectionId && sectionOrder.includes(section.sectionId);
        const isHubBannerByName = section.sectionName?.toLowerCase() === 'hub banner' && sectionOrder.includes('hub-banner');

        // Check if this section was already added to reorderedSections
        const alreadyInReordered = reorderedSections.some(s =>
          (s.sectionId && section.sectionId && s.sectionId === section.sectionId) ||
          (s.sectionName && section.sectionName && s.sectionName === section.sectionName)
        );

        if (!isAlreadyAdded && !isHubBannerByName && !alreadyInReordered) {
          // Add section with a generated ID if it doesn't have one
          reorderedSections.push({
            ...section,
            sectionId: section.sectionId || `cms-section-${index}`,
          });
        }
      });

      return reorderedSections;
    })()
  } : enhancedPageData;

  return (
    <div className={css.landingPage}>
      <PageBuilder
        pageAssetsData={pageDataWithSuggested}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
        useLandingBackground={true}
        isAuthenticated={isAuthenticated}
        options={{
          sectionComponents: {
            customHero: { component: SectionHeroCustom },
            customCategory: { component: SectionCategory },
            customSuggested: { component: SectionSuggested },
            customTopPicks: { component: SectionTopPicks },
            customFeatureGrid: { component: SectionFeatureGrid },
          },
        }}
      />
    </div>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { isAuthenticated } = state.auth;
  const {
    suggestedListings,
    suggestedListingsInProgress,
    featureGridListings,
    featureGridListingsInProgress,
    featureGridIsPersonalized,
    featureGridDebugInfo,
    topPicksListings,
    topPicksInProgress,
    topPicksFetched,
  } = state.LandingPage || {};

  return {
    pageAssetsData,
    inProgress,
    error,
    isAuthenticated,
    suggestedListings,
    suggestedListingsInProgress,
    featureGridListings,
    featureGridListingsInProgress,
    featureGridIsPersonalized,
    featureGridDebugInfo,
    topPicksListings,
    topPicksInProgress,
    topPicksFetched,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const mapDispatchToProps = dispatch => ({
  onFetchTopPicks: () => dispatch(fetchTopPicksListings()),
});

const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
